import { useEffect } from "react"
import { ChoiceField } from "@/components/forms/ChoiceField"
import { Form } from "@/components/forms/Form"
import type { LicenseData } from "@/hooks/useLicenseStore"
import { useStrings, type BuilderStrings } from "@/hooks/useStrings"
import type { UseFormReturn } from "react-hook-form"
import { LicenseLogo } from "@/components/common/LicenseLogo"
import { type LogoKey, type LogoVariant } from "@/lib/licenseLogos"

type SharingProps = {
  form: UseFormReturn<LicenseData>
}

const choice = (strings: BuilderStrings, key: string, logo: LogoKey, logoVariant:LogoVariant = 'color') => ({
  title: strings.sharingTitle?.[key] || "",
  value: key,
  description: strings.sharingDetail?.[key] || "",
  icon: <LicenseLogo logo={logo as any} variant={logoVariant} />
})
const keys: Array<{ key: string; logo: string }> = [
  { key: "accessDirect", logo: "sharing-direct" },
  { key: "accessCollaborative", logo: "sharing-collaborative" },
  { key: "accessFederated", logo: "sharing-federated" },
  { key: "accessTRE", logo: "sharing-tre" }
]

export const Sharing = ({ form }: SharingProps) => {
  const strings = useStrings()
  const onwardSharingValue = form.watch("onwardSharing")
  const onwardSharing = onwardSharingValue === "yes"

  useEffect(() => {
    if (onwardSharingValue !== "no") return
    for (const field of ["shareAlike", "modifications"]) {
      if (form.getValues(field) !== "no") {
        form.setValue(field, "no", { shouldDirty: true, shouldValidate: true })
      }
    }
  }, [form, onwardSharingValue])
  const requiredLabel = (label: string) => `${label} *`

  const yesNo = (
    key: string,
    icons?: { yes?: LogoKey; no?: LogoKey; yesVariant?: "color" | "bw"; noVariant?: "color" | "bw" },
    labelOverride?: (label: string) => string
  ) => {
    const label = strings.title[`${key}Prompt`]
    return (
      <ChoiceField
        form={form}
        name={key}
        label={labelOverride ? labelOverride(label) : label}
        description={strings.question[`${key}Prompt`]}
        behaviour="radio"
        options={[
          {
            title: strings.option.yes,
            description: (strings.affirmation[key] as string[])[0],
            value: "yes",
            icon: icons?.yes ? (
              <LicenseLogo logo={icons.yes} variant={icons.yesVariant ?? "color"} />
            ) : undefined
          },
          {
            title: strings.option.no,
            description: (strings.affirmation[key] as string[])[1],
            value: "no",
            icon: icons?.no ? (
              <LicenseLogo logo={icons.no} variant={icons.noVariant ?? "color"} />
            ) : undefined
          }
        ]}
      />
    )
  }

  return (
    <Form form={form}>
      <ChoiceField
        form={form}
        name="accessMode"
        label={requiredLabel(strings.title.accessModePrompt)}
        description={strings.question.accessModePrompt}
        behaviour="checkbox"
        options={keys.map(item => choice(strings, item.key, item.logo, 'bw'))}
        // className="lg:grid-cols-1"
        // optionClassName="items-center"
      />
      {yesNo("commercialUse", { yes: "commercial-yes", no: "commercial-no" }, requiredLabel)}
      {yesNo("onwardSharing", { yes: "sharing-onward", no: "sharing-no-onward" }, requiredLabel)}
      {onwardSharing ? yesNo("shareAlike", { yes: "share-alike" }) : <></>}
      {onwardSharing ? yesNo("modifications", { no: "modifications-no" }) : <></>}
      {/*
      <RadioField
        form={form}
        name="onwardSharing"
        label={strings.title.onwardSharingPrompt}
        description={strings.question.onwardSharingPrompt}
        options={[strings.option.no, strings.option.yes].filter(Boolean)}
        layout="inline"
        defaultValue={strings.option.no
        }
      /> */}

    </Form>
  )
}
